import {
  Card,
  type CardProps,
  DataStatus,
  DropdownActionMenuItem,
  EmptyStatus,
} from 'platform/components';
import {Center, Hide, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {find, isNil} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CustomerResponseBodyV2,
  useGetCustomerV2Query,
  useGetVehicleCustomerQuery,
} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {buildArray, noop, suffixTestId, TestIdProps} from 'shared/web';

import {OwnershipType} from '../../CustomerVehicleSelection/types/ownershipType';
import {CustomerWidgetCard} from '../../CustomerWidgetCard/CustomerWidgetCard';
import {getOwnershipTitle} from '../utils/getOwnershipTitle';

interface OwnershipCustomerCardProps extends TestIdProps {
  ownershipType: OwnershipType;
  vehicleId: string;
  resourceId: EntityResourceIds;
  recordId: string;
  onDelete?: () => void;
  onChange: () => void;
  onCopy?: (customer: CustomerResponseBodyV2) => void;
  onMove?: (customer: CustomerResponseBodyV2) => void;
  visibility?: {
    isVisible: boolean;
    onChange: (isVisible: boolean) => void;
  };
  colorStripe?: CardProps['colorStripe'];
  isAssignmentDisabled?: boolean;
  isFirstAssignment?: boolean;
}

export function OwnershipCustomerCard(props: OwnershipCustomerCardProps) {
  const {
    selectedOwnership,
    isSelectedOwnershipLoading,
    isSelectedOwnershipError,
    isSelectedOwnershipFetching,
  } = useGetVehicleCustomerQuery(
    {vehicleId: props.vehicleId},
    {
      skip: !props.vehicleId,
      selectFromResult: ({data, isLoading, isError, isFetching}) => ({
        selectedOwnership: find(
          (ownership) => ownership?.customerType === props.ownershipType,
          data ?? []
        ),
        isSelectedOwnershipLoading: isLoading,
        isSelectedOwnershipError: isError,
        isSelectedOwnershipFetching: isFetching,
      }),
    }
  );
  const {
    currentData: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
    isFetching: isCustomerFetching,
  } = useGetCustomerV2Query(
    {customerId: selectedOwnership?.customerId ?? ''},
    {skip: !selectedOwnership?.customerId}
  );

  const title = getOwnershipTitle(props.ownershipType);

  const emptyMessage = match(props.ownershipType)
    .with('CORVEHICLECUSTOMERTYPE_OPERATOR', () =>
      i18n.t('entity.ownership.label.emptyVehicleOperator')
    )
    .with('CORVEHICLECUSTOMERTYPE_OWNER', () => i18n.t('entity.ownership.label.emptyVehicleOwner'))
    .with('CORVEHICLECUSTOMERTYPE_USER', () => i18n.t('entity.ownership.label.emptyVehicleUser'))
    .with('CORVEHICLECUSTOMERTYPE_MANAGER', () =>
      i18n.t('entity.ownership.label.emptyVehicleManager')
    )
    .with('CORVEHICLECUSTOMERTYPE_INVOICE', () =>
      i18n.t('entity.ownership.label.emptyDefaultRecepient')
    )
    .exhaustive();

  const emptyActionTitle = match(props.ownershipType)
    .with('CORVEHICLECUSTOMERTYPE_OPERATOR', () =>
      i18n.t('entity.ownership.actions.assignVehicleOperator')
    )
    .with('CORVEHICLECUSTOMERTYPE_OWNER', () =>
      i18n.t('entity.ownership.actions.assignVehicleOwner')
    )
    .with('CORVEHICLECUSTOMERTYPE_USER', () => i18n.t('entity.ownership.actions.assignVehicleUser'))
    .with('CORVEHICLECUSTOMERTYPE_MANAGER', () =>
      i18n.t('entity.ownership.actions.assignVehicleManager')
    )
    .with('CORVEHICLECUSTOMERTYPE_INVOICE', () =>
      i18n.t('entity.ownership.label.assignDefaultRecepient')
    )
    .exhaustive();

  const tooltip = match(props.ownershipType)
    .with('CORVEHICLECUSTOMERTYPE_OPERATOR', () =>
      i18n.t('entity.ownership.label.tooltipVehicleOperator')
    )
    .with('CORVEHICLECUSTOMERTYPE_OWNER', () =>
      i18n.t('entity.ownership.label.tooltipVehicleOwner')
    )
    .with('CORVEHICLECUSTOMERTYPE_USER', () => i18n.t('entity.ownership.label.tooltipVehicleUser'))
    .with('CORVEHICLECUSTOMERTYPE_MANAGER', () =>
      i18n.t('entity.ownership.label.tooltipVehicleManager')
    )
    .with('CORVEHICLECUSTOMERTYPE_INVOICE', () =>
      i18n.t('entity.ownership.label.tooltipDefaultRecepient')
    )
    .exhaustive();

  const isLoading =
    isCustomerLoading ||
    isSelectedOwnershipLoading ||
    isCustomerFetching ||
    isSelectedOwnershipFetching;
  const isError = isCustomerError || isSelectedOwnershipError;
  const hasCustomerAssignment = isNotNilOrEmpty(selectedOwnership?.customerId);
  const isVisible = isNil(props.visibility) || hasCustomerAssignment || props.visibility.isVisible;
  const visibilityControl: CardProps['control'] = isNotNil(props.visibility)
    ? {
        type: 'switch',
        value: isVisible,
        onChange: props.visibility.onChange,
        isDisabled:
          hasCustomerAssignment || (props.isAssignmentDisabled && !props.visibility.isVisible),
      }
    : undefined;

  return (
    <section aria-label={title}>
      <Show when={customer}>
        <CustomerWidgetCard
          customActions={buildArray<DropdownActionMenuItem>()
            .when(isNotNil(props.onCopy), {
              label: i18n.t('general.actions.copyTo'),
              onClick: () => {
                if (isNil(customer)) {
                  return;
                }
                props.onCopy?.(customer);
              },
            })
            .when(isNotNil(props.onMove), {
              label: i18n.t('general.actions.move'),
              onClick: () => {
                if (isNil(customer)) {
                  return;
                }
                props.visibility?.onChange(true);
                props.onMove?.(customer);
              },
            })
            .when(isNotNil(props.onDelete), {
              label: i18n.t('general.actions.delete'),
              severity: 'danger',
              onClick: () => {
                props.visibility?.onChange(true);
                props.onDelete?.();
              },
            })}
          customTitle={title}
          customerId={customer?.id}
          onCustomer={noop}
          onChange={props.onChange}
          secondStepComponentType="SERVICE_CASE"
          hasVerification
          isDetailLinkVisible
          recordId={props.recordId}
          resourceId={props.resourceId}
          tooltip={tooltip}
          control={visibilityControl}
          colorStripe={props.colorStripe}
          data-testid={suffixTestId('selectedCustomer', props)}
        />
      </Show>
      <Hide when={customer}>
        <Card
          tooltip={tooltip}
          title={title}
          colorStripe={props.colorStripe}
          control={visibilityControl}
          data-testid={suffixTestId('header', props)}
        >
          {isVisible ? (
            <DataStatus isLoading={isLoading} isError={isError} minHeight={33}>
              <Center minHeight={33}>
                <EmptyStatus
                  headline={
                    props.isFirstAssignment
                      ? i18n.t('entity.ownership.label.assignVehicleOperatorFirst')
                      : emptyMessage
                  }
                  action={{
                    title: emptyActionTitle,
                    onClick: props.onChange,
                    isDisabled: props.isAssignmentDisabled,
                    variant:
                      props.ownershipType === 'CORVEHICLECUSTOMERTYPE_OPERATOR'
                        ? 'primary'
                        : 'link',
                  }}
                  data-testid={suffixTestId('header', props)}
                />
              </Center>
            </DataStatus>
          ) : undefined}
        </Card>
      </Hide>
    </section>
  );
}
